import { useEffect, useMemo } from "react";

import type { CreativeBlock } from "../guidedFlow/types";
import type { InstagramAccount } from "../types";
import { resolveInstagramAccountForAdvertiserPage } from "./creativesWithInstagramFromFetch";

export type PartnershipAdvertiserIdentity = {
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
};

export type PartnershipPartnerOptionsFetchParams = {
  ad_account_id: string;
  advertiser_page_id?: string;
  advertiser_instagram_user_id?: string;
};

export type RefetchPartnershipPartnerOptions = (
  args: PartnershipPartnerOptionsFetchParams,
  options?: { force?: boolean }
) => Promise<void>;

/** Stable cache/dedupe key for partnership_partner_options requests. */
export function partnershipPartnerOptionsFetchKey(
  args: PartnershipPartnerOptionsFetchParams
): string {
  return [
    args.ad_account_id.trim(),
    args.advertiser_page_id?.trim() ?? "",
    args.advertiser_instagram_user_id?.trim() ?? "",
  ].join(":");
}

function advertiserFacebookPageFromCreative(
  creative: Pick<CreativeBlock, "fbPage">
): CreativeBlock["fbPage"] {
  if (typeof creative.fbPage === "object") {
    return creative.fbPage ?? null;
  }
  const pageId = String(creative.fbPage ?? "").trim();
  return pageId ? ({ id: pageId, name: "", picture: "" } as const) : null;
}

/** Advertiser page + IG for partnership APIs; falls back to page-linked IG when the select is unset. */
export function resolveAdvertiserIdentityForPartnership(
  creative: Pick<CreativeBlock, "fbPage" | "instagramAccount">,
  catalogAccounts?: readonly InstagramAccount[]
): PartnershipAdvertiserIdentity {
  const page = advertiserFacebookPageFromCreative(creative);
  const pageId = page?.id?.trim();
  const selectedInstagramUserId = creative.instagramAccount?.id?.trim();
  if (selectedInstagramUserId) {
    return {
      advertiserPageId: pageId || undefined,
      advertiserInstagramUserId: selectedInstagramUserId,
    };
  }

  const linkedFromCatalog =
    page && catalogAccounts?.length
      ? resolveInstagramAccountForAdvertiserPage(page, catalogAccounts)
          ?.id?.trim()
      : undefined;
  const linkedInstagramUserId =
    linkedFromCatalog || page?.linked_instagram_user_id?.trim() || undefined;

  return {
    advertiserPageId: pageId || undefined,
    advertiserInstagramUserId: linkedInstagramUserId,
  };
}

export function advertiserIdentityFromCreativeBlock(
  creative: Pick<CreativeBlock, "fbPage" | "instagramAccount">
): PartnershipAdvertiserIdentity {
  return resolveAdvertiserIdentityForPartnership(creative);
}

export function buildPartnershipPartnerOptionsFetchParamsFromCreative(args: {
  adAccountId?: string;
  creative: Pick<CreativeBlock, "fbPage" | "instagramAccount">;
  advertiserInstagramCatalog?: readonly InstagramAccount[];
}): PartnershipPartnerOptionsFetchParams | null {
  const identity = resolveAdvertiserIdentityForPartnership(
    args.creative,
    args.advertiserInstagramCatalog
  );
  return buildPartnershipPartnerOptionsFetchParams({
    adAccountId: args.adAccountId,
    advertiserPageId: identity.advertiserPageId,
    advertiserInstagramUserId: identity.advertiserInstagramUserId,
  });
}

export function collectUniquePartnershipAdvertiserIdentities<
  T extends {
    fbPage?: CreativeBlock["fbPage"];
    instagramAccount?: CreativeBlock["instagramAccount"];
  },
>(
  creatives: readonly T[],
  partnerPostActive: (creative: T) => boolean
): PartnershipAdvertiserIdentity[] {
  const seen = new Set<string>();
  const identities: PartnershipAdvertiserIdentity[] = [];

  for (const creative of creatives) {
    if (!partnerPostActive(creative)) {
      continue;
    }
    const identity = resolveAdvertiserIdentityForPartnership({
      fbPage: creative.fbPage ?? null,
      instagramAccount: creative.instagramAccount ?? null,
    });
    if (!identity.advertiserPageId && !identity.advertiserInstagramUserId) {
      continue;
    }
    const key = [
      identity.advertiserPageId ?? "",
      identity.advertiserInstagramUserId ?? "",
    ].join(":");
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    identities.push(identity);
  }

  return identities;
}

export function buildPartnershipPartnerOptionsFetchParams(args: {
  adAccountId?: string;
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
}): PartnershipPartnerOptionsFetchParams | null {
  const ad_account_id = args.adAccountId?.trim();
  if (!ad_account_id) {
    return null;
  }
  const advertiser_instagram_user_id =
    args.advertiserInstagramUserId?.trim() || undefined;
  const advertiser_page_id = args.advertiserPageId?.trim() || undefined;
  return {
    ad_account_id,
    ...(advertiser_page_id ? { advertiser_page_id } : {}),
    ...(advertiser_instagram_user_id
      ? { advertiser_instagram_user_id }
      : {}),
  };
}

/**
 * Partnership catalog loading policy:
 * - Parent screens load the advertiser identity catalog once per ad account (see AdsEngagePage
 *   refetchRelated, CampaignDetail mount effect).
 * - Refetches the full catalog while partner post is active, debounced per unique advertiser
 *   identity (page + Instagram) across creatives.
 */
export function useDebouncedPartnershipPartnerOptionsLoads({
  enabled,
  partnerPostActive,
  adAccountId,
  advertiserIdentities,
  onLoad,
  debounceMs = 350,
}: {
  enabled: boolean;
  partnerPostActive: boolean;
  adAccountId?: string;
  advertiserIdentities: readonly PartnershipAdvertiserIdentity[];
  onLoad?: RefetchPartnershipPartnerOptions;
  debounceMs?: number;
}): void {
  const identitiesKey = useMemo(
    () =>
      advertiserIdentities
        .map((identity) =>
          partnershipPartnerOptionsFetchKey({
            ad_account_id: adAccountId ?? "",
            advertiser_page_id: identity.advertiserPageId,
            advertiser_instagram_user_id: identity.advertiserInstagramUserId,
          })
        )
        .sort()
        .join("|"),
    [adAccountId, advertiserIdentities]
  );

  useEffect(() => {
    if (!enabled || !partnerPostActive || !onLoad) {
      return;
    }
    const timeout = window.setTimeout(() => {
      for (const identity of advertiserIdentities) {
        const params = buildPartnershipPartnerOptionsFetchParams({
          adAccountId,
          advertiserPageId: identity.advertiserPageId,
          advertiserInstagramUserId: identity.advertiserInstagramUserId,
        });
        if (
          !params?.advertiser_page_id &&
          !params?.advertiser_instagram_user_id
        ) {
          continue;
        }
        void onLoad(params);
      }
    }, debounceMs);
    return () => window.clearTimeout(timeout);
  }, [
    adAccountId,
    advertiserIdentities,
    debounceMs,
    enabled,
    identitiesKey,
    onLoad,
    partnerPostActive,
  ]);
}

/** @deprecated Prefer {@link useDebouncedPartnershipPartnerOptionsLoads} for multi-creative flows. */
export function useDebouncedPartnershipPartnerOptionsLoad({
  enabled,
  partnerPostActive,
  adAccountId,
  advertiserPageId,
  advertiserInstagramUserId,
  onLoad,
  debounceMs = 350,
}: {
  enabled: boolean;
  partnerPostActive: boolean;
  adAccountId?: string;
  advertiserPageId?: string;
  advertiserInstagramUserId?: string;
  onLoad?: RefetchPartnershipPartnerOptions;
  debounceMs?: number;
}): void {
  const identityKey = useMemo(
    () =>
      partnershipPartnerOptionsFetchKey({
        ad_account_id: adAccountId ?? "",
        advertiser_page_id: advertiserPageId,
        advertiser_instagram_user_id: advertiserInstagramUserId,
      }),
    [adAccountId, advertiserPageId, advertiserInstagramUserId]
  );

  useEffect(() => {
    if (!enabled || !partnerPostActive || !onLoad) {
      return;
    }
    const params = buildPartnershipPartnerOptionsFetchParams({
      adAccountId,
      advertiserPageId,
      advertiserInstagramUserId,
    });
    if (!params?.advertiser_page_id && !params?.advertiser_instagram_user_id) {
      return;
    }
    const timeout = window.setTimeout(() => {
      void onLoad(params);
    }, debounceMs);
    return () => window.clearTimeout(timeout);
  }, [
    adAccountId,
    advertiserInstagramUserId,
    advertiserPageId,
    debounceMs,
    enabled,
    identityKey,
    onLoad,
    partnerPostActive,
  ]);
}
